import { TripStatus } from "../services/trackTripService";

interface StatusMeta {
  label: string;
  description: string;
  /** Color del badge del estado. */
  color: string;
  /** Estado terminal: el viaje terminó (de forma exitosa o no). */
  terminal: boolean;
}

export const STATUS_META: Record<TripStatus, StatusMeta> = {
  created: {
    label: "Creando viaje",
    description: "Estamos registrando tu viaje.",
    color: "#3EC585",
    terminal: false,
  },
  searching: {
    label: "Buscando chofer",
    description: "Estamos buscando un chofer cercano para vos.",
    color: "#3EC585",
    terminal: false,
  },
  assigned: {
    label: "Conductor asignado",
    description: "Un chofer aceptó tu viaje y está en camino.",
    color: "#1D6BD7",
    terminal: false,
  },
  started: {
    label: "En camino",
    description: "Tu chofer está yendo hacia el punto de encuentro.",
    color: "#F28929",
    terminal: false,
  },
  waiting: {
    label: "Tu chofer llegó",
    description: "Tu chofer llegó y te está esperando.",
    color: "#FDBF4C",
    terminal: false,
  },
  arrived: {
    label: "En destino",
    description:
      "Estás llegando a tu destino. Antes de bajarte, revisá que tengas todas tus cosas con vos.",
    color: "#108C62",
    terminal: false,
  },
  carrying: {
    label: "Viaje en curso",
    description: "Estás en viaje hacia tu destino.",
    color: "#108C62",
    terminal: false,
  },
  finished: {
    label: "Viaje finalizado",
    description: "Llegaste a destino. ¡Gracias por viajar con ViajáYa!",
    color: "#525252",
    terminal: true,
  },
  cancelled: {
    label: "Viaje cancelado",
    description: "Este viaje fue cancelado.",
    color: "#E44747",
    terminal: true,
  },
  autocancelled: {
    label: "Viaje cancelado",
    description: "Este viaje se canceló automáticamente.",
    color: "#951F1F",
    terminal: true,
  },
  rejected: {
    label: "Viaje rechazado",
    description: "El viaje fue rechazado.",
    color: "#E44747",
    terminal: true,
  },
  unassigned: {
    label: "Sin chofer",
    description: "El chofer ya no está asignado a este viaje.",
    color: "#FDBF4C",
    terminal: false,
  },
  retrieved: {
    label: "Reasignando viaje",
    description: "Estamos reasignando tu viaje a otro chofer.",
    color: "#0A6B40",
    terminal: false,
  },
  emergency: {
    label: "Emergencia",
    description: "Se reportó una emergencia en este viaje.",
    color: "#E44747",
    terminal: true,
  },
};

const FALLBACK_META: StatusMeta = {
  label: "Estado del viaje",
  description: "Seguimiento de tu viaje.",
  color: "#3EC585",
  terminal: false,
};

export const getStatusMeta = (status: TripStatus): StatusMeta =>
  STATUS_META[status] ?? FALLBACK_META;

export const getStatusColor = (status: TripStatus): string =>
  getStatusMeta(status).color;

/** Devuelve negro o blanco según cuál contraste mejor con el color del estado. */
export const getStatusTextColor = (status: TripStatus): string => {
  const hex = getStatusColor(status).replace(/^#/, "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Luminancia relativa percibida (ITU-R BT.601).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#141414" : "#FFFFFF";
};
